import React, { useEffect, useState } from "react";
import { db } from "../../firebase/config";
import { collection, getDocs, doc, updateDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";

const AdminSubscriptionPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  // Fetch all plans from the subscription_plans collection
  const fetchPlans = async () => {
    try {
      setLoading(true);
      const plansRef = collection(db, "subscription_plans");
      const snapshot = await getDocs(plansRef);
      const plansArr = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
      setPlans(plansArr);
    } catch (error) {
      console.error("Error fetching plans:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const handleChange = (planId, field, value) => {
    setPlans(prevPlans => prevPlans.map(plan =>
      plan.id === planId ? { ...plan, [field]: value } : plan
    ));
  };

  const saveChanges = async () => {
    setSaving(true);
    try {
      // Update each plan document in Firestore
      for (const plan of plans) {
        const planRef = doc(db, "subscription_plans", plan.id);
        // Ensure rotisPerDay is a number
        const rotisPerDayNum = Number(plan.rotisPerDay);
        // Update the first item in details array to match rotisPerDay
        let detailsArr = Array.isArray(plan.details) ? [...plan.details] : [];
        if (detailsArr.length > 0) {
          detailsArr[0] = `🥟 ${rotisPerDayNum} rotis/day`;
        }
        // Only update editable fields
        await updateDoc(planRef, {
          price: Number(plan.price),
          name: plan.name,
          rotisPerDay: rotisPerDayNum,
          details: detailsArr,
          note: plan.note,
          isActive: plan.isActive !== false // default to true if not specified
        });
      }
      alert("✅ Plans updated successfully!");
      fetchPlans(); // Refresh after save
    } catch (error) {
      console.error("Error saving plans:", error);
      alert("❌ Failed to save plans.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-4">Loading plans...</div>;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <button
        onClick={() => navigate('/admin/dashboard')}
        className="mb-4 px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
      >
        ← Back to Admin Dashboard
      </button>
      <h2 className="text-2xl font-semibold mb-6">🛠️ Manage Subscription Plans</h2>

      {plans.map((plan) => (
        <div key={plan.id} className="mb-4 border rounded-lg p-4 bg-white shadow">
          <h3 className="text-xl font-medium capitalize mb-2">{plan.name} Plan</h3>
          <div className="space-y-2">
            <label className="block">
              <span className="text-sm text-gray-700">Name</span>
              <input
                type="text"
                className="w-full border p-2 rounded"
                value={plan.name}
                onChange={e => handleChange(plan.id, "name", e.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-sm text-gray-700">Price (₹)</span>
              <input
                type="number"
                className="w-full border p-2 rounded"
                value={plan.price}
                onChange={e => handleChange(plan.id, "price", e.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-sm text-gray-700">Rotis Per Day</span>
              <input
                type="number"
                className="w-full border p-2 rounded"
                value={plan.rotisPerDay}
                onChange={e => handleChange(plan.id, "rotisPerDay", e.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-sm text-gray-700">Details (comma separated)</span>
              <input
                type="text"
                className="w-full border p-2 rounded"
                value={Array.isArray(plan.details) ? plan.details.join(", ") : plan.details}
                onChange={e => handleChange(plan.id, "details", e.target.value.split(",").map(s => s.trim()))}
              />
            </label>
            <label className="block">
              <span className="text-sm text-gray-700">Note</span>
              <input
                type="text"
                className="w-full border p-2 rounded"
                value={plan.note || ""}
                onChange={e => handleChange(plan.id, "note", e.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-sm text-gray-700">Active</span>
              <input
                type="checkbox"
                checked={plan.isActive !== false}
                onChange={e => handleChange(plan.id, "isActive", e.target.checked)}
                className="ml-2"
              />
              <span className="ml-2 text-xs text-gray-500">(Uncheck to hide this plan from customers)</span>
            </label>
          </div>
        </div>
      ))}

      <button
        onClick={saveChanges}
        disabled={saving}
        className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
      >
        {saving ? "Saving..." : "💾 Save Changes"}
      </button>
    </div>
  );
};

export default AdminSubscriptionPlans; 